import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShopError, api, type GlossaryIndexEntry } from '../api';
import { useT } from '../i18n';
import { usePageMeta, useJsonLd, absolute } from '../seo';

/**
 * Slovník pojmů – rejstřík A–Z.
 *
 * Rozcestník obsahových hesel („grain free", „taurin", „odčervení"), který
 * přivádí návštěvnost na informační dotazy. České řazení MUSÍ správně třídit
 * diakritiku (Č za C, CH za H, Ř, Š, Ž) – používá `Intl.Collator('cs')`,
 * nikdy prostý `sort()`.
 */

/** Odstraní diakritiku a sjednotí velikost – jen pro porovnání při hledání. */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase();
}

/**
 * Do jakého písmene rejstříku heslo patří. Digraf „ch" je v češtině
 * samostatné písmeno za H; číslice a symboly padnou do koše „#".
 */
function bucketOf(term: string): string {
  const trimmed = term.trimStart();
  if (trimmed === '') return '#';
  if (trimmed.slice(0, 2).toLocaleLowerCase('cs') === 'ch') return 'Ch';
  const first = trimmed[0]!.toLocaleUpperCase('cs');
  return /\p{L}/u.test(first) ? first : '#';
}

interface Bucket {
  readonly letter: string;
  readonly terms: readonly GlossaryIndexEntry[];
}

export function GlossaryIndexPage() {
  const { t } = useT();
  const [terms, setTerms] = useState<readonly GlossaryIndexEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    api
      .listGlossary()
      .then((result) => {
        if (alive) {
          setTerms(result.terms);
          setError(null);
        }
      })
      .catch((caught: unknown) => {
        if (alive) setError(caught instanceof ShopError ? caught.message : 'Načtení se nezdařilo.');
      });
    return () => {
      alive = false;
    };
  }, []);

  usePageMeta({
    title: `${t('glossary.title')} | Super Krmivo`,
    description: t('glossary.metaDescription'),
  });

  // Strukturovaná data: rejstřík jako DefinedTermSet s odkazy na hesla.
  useJsonLd(
    terms === null || terms.length === 0
      ? null
      : {
          '@context': 'https://schema.org',
          '@type': 'DefinedTermSet',
          name: t('glossary.title'),
          url: absolute('/slovnik'),
          hasDefinedTerm: terms.map((entry) => ({
            '@type': 'DefinedTerm',
            name: entry.term,
            ...(entry.excerpt === '' ? {} : { description: entry.excerpt }),
            url: absolute(`/slovnik/${entry.slug}`),
          })),
        },
  );

  const collator = useMemo(() => new Intl.Collator('cs'), []);

  const buckets = useMemo<readonly Bucket[]>(() => {
    if (terms === null) return [];
    const needle = fold(query.trim());
    const filtered =
      needle === ''
        ? terms
        : terms.filter(
            (entry) =>
              fold(entry.term).includes(needle) ||
              entry.aliases.some((alias) => fold(alias).includes(needle)),
          );

    const byLetter = new Map<string, GlossaryIndexEntry[]>();
    for (const entry of filtered) {
      const letter = bucketOf(entry.term);
      const list = byLetter.get(letter) ?? [];
      list.push(entry);
      byLetter.set(letter, list);
    }

    const letters = [...byLetter.keys()].sort((a, b) => {
      // Koš „#" (číslice a symboly) vždy až na konec.
      if (a === '#') return 1;
      if (b === '#') return -1;
      return collator.compare(a, b);
    });

    return letters.map((letter) => ({
      letter,
      terms: (byLetter.get(letter) ?? []).slice().sort((a, b) => collator.compare(a.term, b.term)),
    }));
  }, [terms, query, collator]);

  return (
    <article className="glossary">
      <header className="glossary-head">
        <h1>{t('glossary.title')}</h1>
        <p className="muted">{t('glossary.intro')}</p>
      </header>

      <div className="glossary-search">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('glossary.searchPlaceholder')}
          aria-label={t('glossary.searchLabel')}
        />
      </div>

      {terms === null && error === null && <p className="muted">{t('glossary.loading')}</p>}
      {error !== null && <p className="muted">{error}</p>}
      {terms !== null && terms.length === 0 && <p className="empty muted">{t('glossary.empty')}</p>}
      {terms !== null && terms.length > 0 && buckets.length === 0 && (
        <p className="empty muted">{t('glossary.noResults')}</p>
      )}

      {buckets.length > 1 && (
        <nav className="glossary-jump" aria-label={t('glossary.title')}>
          {buckets.map((bucket) => (
            <a key={bucket.letter} href={`#pismeno-${bucket.letter}`}>
              {bucket.letter}
            </a>
          ))}
        </nav>
      )}

      {buckets.map((bucket) => (
        <section key={bucket.letter} className="glossary-section" id={`pismeno-${bucket.letter}`}>
          <h2 className="glossary-letter">{bucket.letter}</h2>
          <ul className="glossary-terms">
            {bucket.terms.map((entry) => (
              <li key={entry.slug}>
                <Link className="glossary-term-link" to={`/slovnik/${entry.slug}`}>
                  <strong>{entry.term}</strong>
                  {entry.excerpt !== '' && <span className="muted">{entry.excerpt}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </article>
  );
}
